import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export interface ChurnRate {
  churnRate: number;
  periodDays: number;
  cancelled: number;
  active: number;
  retentionRate: number;
}

export interface RetentionMetrics {
  monthly: {
    month: string;
    retained: number;
    churned: number;
    retentionRate: number;
  }[];
  totalRetained: number;
  totalChurned: number;
  averageRetentionRate: number;
}

export interface ConversionFunnel {
  visitors: number;
  followers: number;
  membershipInitiations: number;
  completedMemberships: number;
  conversionRates: {
    followerToInitiation: number;
    initiationToCompletion: number;
    overallConversion: number;
  };
}

export interface RevenueForecast {
  forecast: {
    month: string;
    projected: number;
    confidence: string;
  }[];
  growthRate: number;
  averageMonthlyGrowth: number;
}

export interface MemberLifetimeValue {
  averageLTV: number;
  medianLTV: number;
  distribution: { range: string; count: number; percentage: number }[];
  totalLTV: number;
  memberCount: number;
}

export interface AnalyticsSummary {
  totalRevenue: number;
  activePatrons: number;
  monthlyRecurringRevenue: number;
  averageLTV: number;
  churnRate: number;
}

export interface ComprehensiveAnalytics {
  churnRate: ChurnRate;
  retention: RetentionMetrics;
  conversionFunnel: ConversionFunnel;
  revenueForecast: RevenueForecast;
  memberLTV: MemberLifetimeValue;
  summary?: AnalyticsSummary;
}

export const analyticsService = {
  async getAnalytics(creatorId: string, periodDays: number = 30) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/creator/${creatorId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { periodDays },
    });
    return res.data as { analytics: ComprehensiveAnalytics };
  },

  async getChurnRate(creatorId: string, periodDays: number = 30) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/creator/${creatorId}/churn`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { periodDays },
    });
    return res.data as { churnRate: ChurnRate };
  },

  async getRetention(creatorId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/creator/${creatorId}/retention`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { retention: RetentionMetrics };
  },

  async getConversion(creatorId: string, periodDays: number = 30) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/creator/${creatorId}/conversion`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { periodDays },
    });
    return res.data as { conversion: ConversionFunnel };
  },

  async getForecast(creatorId: string, forecastMonths: number = 6) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/creator/${creatorId}/forecast`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { forecastMonths },
    });
    return res.data as { forecast: RevenueForecast };
  },

  async getLTV(creatorId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/creator/${creatorId}/ltv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { ltv: MemberLifetimeValue };
  },
  // New unified dashboard stats (creator perspective)
  async getDashboard() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as any; // DashboardStats shape
  },
  async getGeographicSummary() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/geographic/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { success: boolean; data: any };
  },
  async getEngagementSummary() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/engagement/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { success: boolean; data: any };
  },
  async getRevenueForecastSummary() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/revenue-forecast/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { success: boolean; data: any };
  },
  async getCohortSummary() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/analytics/cohort/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { success: boolean; data: any };
  },
  async exportReport(params: { type: string; format?: 'json' | 'csv'; startDate?: string; endDate?: string }) {
    const token = localStorage.getItem('accessToken');
    if (params.format === 'csv') {
      const res = await axios.post(`${API_BASE_URL}/api/analytics/reporting/generate`, params, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      return res.data as Blob;
    }
    const res = await axios.post(`${API_BASE_URL}/api/analytics/reporting/generate`, params, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data as { success: boolean; data: any[]; meta: any };
  }
};


